import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  indexSymbolsQueryOptions,
  signalSessionsQueryOptions,
  trendSignalsQueryOptions,
} from '@/features/signals/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { TrendQuery } from '@/schemas/signals'
import {
  pagingParams,
  sessionDateParamSchema,
  symbolSearchParamSchema,
} from '@/screens/signals/search'

/**
 * - `date` — the session; absent is mm-be's latest.
 * - `tab` — Positive (the `met` checks) or Negative (`unmet`). Both tabs list
 *   the same stocks A→Z; there is no side filter on the API.
 * - `q` — a symbol prefix.
 * - `page`/`pageSize` — server paging; 15 a page, as the frame draws.
 */
export const trendSignalsSearchSchema = z.object({
  date: sessionDateParamSchema,
  tab: z.enum(['positive', 'negative']).default('positive').catch('positive'),
  q: symbolSearchParamSchema,
  ...pagingParams(),
})
export type TrendSignalsSearch = z.infer<typeof trendSignalsSearchSchema>

/** The read a search names: what the loader warms and the screen keys on. */
export const trendQueryOf = (search: TrendSignalsSearch): TrendQuery => ({
  date: search.date ?? null,
  q: search.q ?? null,
  page: search.page,
  pageSize: search.pageSize,
})

/**
 * The `/market/signals/trend` loader. MAIN: the entry URL's page. SECONDARY:
 * the sessions, and the index symbols (which rows link).
 */
export async function loadTrendSignals({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = trendSignalsSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, signalSessionsQueryOptions())
    prefetchRouteData(queryClient, indexSymbolsQueryOptions())
  }
  await ensureRouteData(queryClient, trendSignalsQueryOptions(trendQueryOf(search)))
}
